import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSnackbar } from 'notistack';

import { useHookForm } from '@monorepo/common';

import { useGetUserInfoQuery } from '../../services/serviceUserInfo';
import { useSubmitContactMutation } from '../../services/serviceContact';
import { RECAPTCHA_PUBLIC_KEY } from '../../constants/constants';
import { IQuestionsForm } from './formInterface';
import { schema } from './schema';
import { HELP_TOPICS } from './topics';
import './questions-modal.css';

interface IQuestionsModalProps {
  open: boolean;
  onClose: () => void;
}

const defaultValues: IQuestionsForm = {
  topic: '',
  name: '',
  email: '',
  message: '',
  website: '',
};

// Bold field label with a required-asterisk, matching the mockup.
const FieldLabel = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className="qm-label"
  >
    {children}
    <span
      className="qm-required"
      aria-hidden="true"
    >
      {' '}
      *
    </span>
  </Typography>
);

export const QuestionsModal = ({ open, onClose }: IQuestionsModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sent, setSent] = useState(false);

  const { data: userInfo } = useGetUserInfoQuery();
  const [submitContact, { isLoading }] = useSubmitContactMutation();

  // Invisible reCAPTCHA — executed on submit, no user friction unless challenged.
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    control,
    handleSubmit,
    errors,
    setValue,
    reset,
  } = useHookForm<IQuestionsForm>({
    schema,
    defaultValues,
  });

  // Prefill Name/Email from the logged-in user (Jobs API is camel-cased).
  useEffect(() => {
    if (open && userInfo?.isLoggedIn) {
      if (userInfo.name) setValue('name', userInfo.name);
      if (userInfo.email) setValue('email', userInfo.email);
    }
  }, [open, userInfo, setValue]);

  // Reset the "sent" confirmation state whenever the modal is re-opened.
  useEffect(() => {
    if (open) setSent(false);
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const onSubmit = async (data: IQuestionsForm) => {
    // Run the invisible reCAPTCHA before hitting the API. Bail with a clear
    // toast if the widget isn't ready or no token comes back.
    if (!recaptchaRef.current) {
      enqueueSnackbar('Could not verify you’re human. Please refresh and try again.', {
        variant: 'error',
      });
      return;
    }

    const captchaToken = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();

    if (!captchaToken) {
      enqueueSnackbar('Captcha token not found. Please try again.', {
        variant: 'error',
      });
      return;
    }

    try {
      const res = await submitContact({
        topic: data.topic,
        name: (data.name || '').trim(),
        email: (data.email || '').trim(),
        message: (data.message || '').trim(),
        website: data.website || '',
        captchaToken,
      }).unwrap();

      if (res?.success) {
        setSent(true);
        reset(defaultValues);
        enqueueSnackbar('Thanks. Your question was sent. We’ll reply within 1 business day.', {
          variant: 'success',
        });
      }
    } catch (err) {
      // baseQueryWithReAuth already toasts every 400 (incl. the captcha "Captcha is
      // invalid" message), so skip our generic fallback for 400s to avoid a double
      // toast. Keep the fallback for network/other errors so the user always gets
      // feedback if the request never reached the API.
      const status = (err as { status?: unknown } | undefined)?.status;
      if (status !== 400) {
        enqueueSnackbar('Something went wrong sending your question. Please try again.', {
          variant: 'error',
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="questions-modal-title"
      maxWidth="md"
      fullWidth
      PaperProps={{ className: 'qm-paper' }}
      BackdropProps={{ className: 'qm-backdrop' }}
    >
      <IconButton
        aria-label="Close"
        onClick={handleClose}
        className="qm-close"
        size="small"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </IconButton>

      {sent ? (
        <Box
          className="qm-body qm-sent"
          role="status"
          aria-live="polite"
        >
          <div
            className="qm-sent-icon"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              width="28"
              height="28"
              fill="none"
            >
              <path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <Typography
            component="h2"
            className="qm-title"
            id="questions-modal-title"
          >
            Thanks, we&rsquo;re on it
          </Typography>
          <Typography className="qm-subtitle">
            A real person will reply within 1 business day.
          </Typography>
          <Button
            type="button"
            variant="contained"
            className="qm-submit"
            onClick={handleClose}
          >
            Close
          </Button>
        </Box>
      ) : (
        <Box className="qm-body">
          <Typography
            component="h2"
            className="qm-title"
            id="questions-modal-title"
          >
            Have questions? We&rsquo;re here to help
          </Typography>
          <Typography className="qm-subtitle">
            Job listings, sales, billing: a real person replies within 1 business day.
          </Typography>

          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            {/* Topic */}
            <div className="qm-field">
              <FieldLabel htmlFor="qm-topic">Topic</FieldLabel>
              <Controller
                control={control}
                name="topic"
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="qm-topic"
                    select
                    required
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    error={!!errors?.topic}
                    helperText={errors?.topic?.message as string}
                    className="qm-input"
                  >
                    <MenuItem
                      value=""
                      disabled
                    >
                      <span className="qm-placeholder">Choose a topic…</span>
                    </MenuItem>
                    {HELP_TOPICS.map((t) => (
                      <MenuItem
                        key={t.key}
                        value={t.key}
                      >
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </div>

            {/* Name + Email — 2-col grid */}
            <div className="qm-row2">
              <div className="qm-field">
                <FieldLabel htmlFor="qm-name">Name</FieldLabel>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="qm-name"
                      required
                      fullWidth
                      placeholder="Your name"
                      autoComplete="name"
                      error={!!errors?.name}
                      helperText={errors?.name?.message as string}
                      className="qm-input"
                    />
                  )}
                />
              </div>

              <div className="qm-field">
                <FieldLabel htmlFor="qm-email">Email</FieldLabel>
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="qm-email"
                      type="email"
                      required
                      fullWidth
                      placeholder="you@company.com"
                      autoComplete="email"
                      error={!!errors?.email}
                      helperText={errors?.email?.message as string}
                      className="qm-input"
                    />
                  )}
                />
              </div>
            </div>

            {/* Message */}
            <div className="qm-field">
              <FieldLabel htmlFor="qm-message">Message</FieldLabel>
              <Controller
                control={control}
                name="message"
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="qm-message"
                    required
                    fullWidth
                    multiline
                    minRows={6}
                    placeholder="Tell us a bit about your question…"
                    error={!!errors?.message}
                    helperText={errors?.message?.message as string}
                    className="qm-input"
                  />
                )}
              />
            </div>

            {/* Honeypot — visually hidden; real users never fill this */}
            <Controller
              control={control}
              name="website"
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="qm-honeypot"
                />
              )}
            />

            {/* Invisible reCAPTCHA — runs on submit, badge bottom-right */}
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={RECAPTCHA_PUBLIC_KEY}
              badge="bottomright"
            />

            <div className="qm-footer">
              <span className="qm-mail">
                or email
                {' '}
                <a href="mailto:hello@cpgjobs.com">hello@cpgjobs.com</a>
              </span>
              <Button
                type="submit"
                variant="contained"
                className="qm-submit"
                disabled={isLoading}
              >
                {isLoading ? 'Sending…' : 'Send request'}
              </Button>
            </div>
          </form>
        </Box>
      )}
    </Dialog>
  );
};

export default QuestionsModal;
